import { OTLayout } from "@/components/layout/ot-layout";
import { useState } from "react";
import { ExternalLink } from "lucide-react";

type FilterOrg = "All" | "RCOT" | "NICE" | "COT" | "BSR" | "EULAR";

const GUIDELINES = [
  {
    org: "RCOT",
    title: "RCOT Code of Ethics and Professional Conduct",
    year: "2021",
    summary: "The professional and ethical framework governing all registered occupational therapists in the UK. Covers principles of autonomy, beneficence, non-maleficence, justice, and professional integrity.",
    relevance: "Core governance document. All OT practice must align with the Code. Referenced in HCPC Fitness to Practise.",
    url: "https://www.rcot.co.uk/practice-resources/rcot-publications/downloads/rcot-code-of-ethics",
    tags: ["Professional Standards", "Ethics"],
  },
  {
    org: "RCOT",
    title: "RCOT Specialist Section in Rheumatology — Practice Guidelines",
    year: "Ongoing",
    summary: "Position statements and clinical guidance from the RCOT Specialist Section in Rheumatology, covering hand assessment, splinting, joint protection education, fatigue management, and MDT working in rheumatology.",
    relevance: "Primary specialist guidance for rheumatology OTs. Informs joint protection, splinting, and fatigue management approaches.",
    url: "https://www.rcot.co.uk/about-us/specialist-sections/specialist-sections-rheumatology",
    tags: ["Rheumatology OT", "Specialist Guidance"],
  },
  {
    org: "RCOT",
    title: "RCOT Practice Guideline: Occupational Therapy for Adults Undergoing Total Hip Replacement (with rheumatology relevance)",
    year: "2020",
    summary: "Guidance on OT assessment, home planning, ADL rehabilitation, and discharge planning for adults undergoing THR — applicable to patients with RA and OA.",
    relevance: "Supports pre- and post-operative OT planning for patients with RA requiring joint replacement.",
    url: "https://www.rcot.co.uk/practice-resources/rcot-publications/downloads/hip-replacement-guideline",
    tags: ["Hip Replacement", "Pre/Post-operative", "ADL"],
  },
  {
    org: "RCOT",
    title: "RCOT Driving and Mobility Guidelines",
    year: "2019",
    summary: "Guidance on OT assessment for fitness to drive, clinical reasoning frameworks, and referral pathways for driving assessment centres.",
    relevance: "Supports OT assessment for patients with RA or axSpA with cervical or upper limb involvement affecting driving.",
    url: "https://www.rcot.co.uk/practice-resources/rcot-publications",
    tags: ["Driving", "Assessment", "Functional"],
  },
  {
    org: "NICE",
    title: "NG100 — Rheumatoid Arthritis in Adults",
    year: "2018 (updated 2023)",
    summary: "Comprehensive NICE guideline for RA management. Includes specific recommendations for OT: hand assessment, splinting for RA flare, joint protection education, fatigue management, AT prescription, and MDT working.",
    relevance: "Primary evidence-based reference for RA OT practice. Cited in RCOT and BSR guidance. Informs OT intervention selection.",
    url: "https://www.nice.org.uk/guidance/ng100",
    tags: ["RA", "Splinting", "Joint Protection", "AT"],
  },
  {
    org: "NICE",
    title: "NG65 — Spondyloarthritis (including Ankylosing Spondylitis)",
    year: "2017",
    summary: "NICE guideline covering axial and peripheral spondyloarthritis. Recommends occupational therapy as part of the MDT, particularly for posture, ADL, and fatigue management.",
    relevance: "Informs OT role in axSpA — posture advice, fatigue management, driving assessment, workplace adaptations.",
    url: "https://www.nice.org.uk/guidance/ng65",
    tags: ["axSpA", "AS", "Fatigue", "Posture"],
  },
  {
    org: "NICE",
    title: "NG206 — Systemic Lupus Erythematosus",
    year: "2023",
    summary: "First NICE guideline specifically for SLE. Recommends OT referral for fatigue management, cognitive difficulties, ADL support, and psychological wellbeing.",
    relevance: "Validates OT role in SLE fatigue management, cognitive strategies, and functional independence.",
    url: "https://www.nice.org.uk/guidance/ng206",
    tags: ["SLE", "Fatigue", "Cognitive", "ADL"],
  },
  {
    org: "NICE",
    title: "NG226 — Osteoarthritis in Adults",
    year: "2022",
    summary: "NICE OA guideline. Recommends OT for hand OA (CMCJ splinting, joint protection, AT) and for generalised functional assessment.",
    relevance: "Supports OT prescription of thumb CMC splints and AT for hand OA in rheumatology patients.",
    url: "https://www.nice.org.uk/guidance/ng226",
    tags: ["OA", "Hand OA", "CMC Splinting"],
  },
  {
    org: "NICE",
    title: "Assistive Technology — MTG51",
    year: "2021",
    summary: "NICE medical technology guidance on digital AT. Endorses OT assessment as the standard pathway for AT prescription. References Living Made Easy (DLF) as a key resource.",
    relevance: "Supports OT role in AT assessment and prescription. References evidence for AT in improving independence.",
    url: "https://www.nice.org.uk/guidance/mtg51",
    tags: ["Assistive Technology", "Digital AT"],
  },
  {
    org: "COT",
    title: "COT Fatigue Management Programme for Adults with Inflammatory Arthritis",
    year: "2020",
    summary: "The College of Occupational Therapists' evidence-based fatigue management programme. Covers pacing, energy conservation, activity scheduling, sleep, and psychoeducation for RA, SLE, and other inflammatory conditions.",
    relevance: "Primary clinical framework for OT fatigue management in rheumatology. Underpins the fatigue management approaches used in clinical practice.",
    url: "https://www.rcot.co.uk/practice-resources/rcot-publications",
    tags: ["Fatigue", "COT Programme", "RA", "SLE"],
  },
  {
    org: "BSR",
    title: "BSR MDT Standards for Rheumatology Services",
    year: "2018",
    summary: "BSR standards for rheumatology multi-disciplinary teams, including the role of OT in the rheumatology MDT — hand therapy, splinting, ADL, AT, patient education.",
    relevance: "Defines OT's place in the rheumatology MDT. Useful for service development and NHS commissioning discussions.",
    url: "https://www.rheumatology.org.uk/practice-quality/guidelines/mdt-standards",
    tags: ["MDT", "Service Standards", "BSR"],
  },
  {
    org: "EULAR",
    title: "EULAR Recommendations for Patient Education in Inflammatory Arthritis",
    year: "2018",
    summary: "EULAR recommendations covering structured patient education programmes, including joint protection education (JPPE), self-management support, and fatigue education in RA, PsA, and axSpA.",
    relevance: "Provides international evidence base for OT patient education programmes (JPPE, fatigue management).",
    url: "https://ard.bmj.com/content/77/8/1131",
    tags: ["Patient Education", "JPPE", "Self-Management"],
  },
  {
    org: "EULAR",
    title: "EULAR Recommendations for Physical Activity in Rheumatic and MSK Diseases",
    year: "2018",
    summary: "EULAR recommendations on physical activity promotion — OT role in activity modification, fatigue management alongside physical activity, and occupation-focused exercise integration.",
    relevance: "Supports OT integration of physical activity within ADL and occupation-focused rehabilitation.",
    url: "https://ard.bmj.com/content/77/9/1249",
    tags: ["Physical Activity", "Exercise", "Fatigue"],
  },
];

export default function OTGuidelines() {
  const [filter, setFilter] = useState<FilterOrg>("All");

  const filtered = GUIDELINES.filter((g) => filter === "All" || g.org === filter);

  return (
    <OTLayout title="RCOT & NICE Guidelines">
      <div className="space-y-8">

        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-sm text-amber-800">
          <strong>Staying up to date:</strong> RCOT guidance is updated regularly. Always verify currency at <a href="https://www.rcot.co.uk" target="_blank" rel="noopener noreferrer" className="underline">rcot.co.uk</a>. NICE guidelines are reviewed biannually — check for updates at <a href="https://www.nice.org.uk" target="_blank" rel="noopener noreferrer" className="underline">nice.org.uk</a>.
        </div>

        {/* Filter */}
        <div className="flex flex-wrap gap-2">
          {(["All", "RCOT", "NICE", "COT", "BSR", "EULAR"] as FilterOrg[]).map((org) => (
            <button
              key={org}
              onClick={() => setFilter(org)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${filter === org ? "bg-amber-600 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-amber-300"}`}
            >
              {org}
            </button>
          ))}
        </div>

        {/* Guidelines list */}
        <div className="space-y-4">
          {filtered.map((g) => (
            <div key={g.title} className="bg-white rounded-2xl border border-slate-200 p-6 space-y-3">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                      g.org === "NICE" ? "bg-blue-100 text-blue-700"
                        : g.org === "RCOT" ? "bg-amber-100 text-amber-700"
                          : g.org === "COT" ? "bg-orange-100 text-orange-700"
                            : g.org === "BSR" ? "bg-slate-100 text-slate-700"
                              : "bg-emerald-100 text-emerald-700"
                    }`}>{g.org}</span>
                    <span className="text-xs text-slate-400">{g.year}</span>
                  </div>
                  <h3 className="font-bold text-slate-900">{g.title}</h3>
                </div>
                <a
                  href={g.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-amber-600 hover:text-amber-700 shrink-0"
                  title="Open guideline"
                >
                  <ExternalLink className="h-5 w-5" />
                </a>
              </div>
              <p className="text-sm text-slate-600">{g.summary}</p>
              <div className="bg-amber-50 rounded-lg px-3 py-2">
                <p className="text-xs text-amber-800"><span className="font-semibold">OT relevance:</span> {g.relevance}</p>
              </div>
              <div className="flex flex-wrap gap-1">
                {g.tags.map((t) => (
                  <span key={t} className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{t}</span>
                ))}
              </div>
            </div>
          ))}
        </div>

      </div>
    </OTLayout>
  );
}
